import { isCancelledError, type QueryClient } from "@tanstack/react-query";

/**
 * One canonical key per piece of data, so two screens that show the same data share one fetch.
 * The first element of each key is its root; invalidation goes by root, so a call site needs no ids.
 */
export const queryKeys = {
  tenants: (sub: string | undefined, tenantIdsCsv: string | undefined) => ["tenants", sub, tenantIdsCsv] as const,
  workspaces: (tenantId: string | null) => ["workspaces", tenantId] as const,
  catalog: (workspaceId: string | null | undefined) => ["catalog", workspaceId] as const,
  versions: (workspaceId: string | null | undefined) => ["versions", workspaceId] as const,
  rules: () => ["rules"] as const,
  lastSync: (tenantId: string | null) => ["last-sync", tenantId] as const,
  openSessions: (workspaceId: string | null | undefined) => ["open-sessions", workspaceId] as const,
  /** The one active session's full row. It shares the open-sessions root on purpose: every action that refreshes the list reads it again. */
  openSession: (sessionId: string | null | undefined) => ["open-sessions", "detail", sessionId] as const,
  tenantOpenSessions: (tenantId: string | null) => ["tenant-open-sessions", tenantId] as const,
  openDrafts: (workspaceId: string | null | undefined) => ["open-drafts", workspaceId] as const,
  agentRuns: (sessionId: string | null | undefined) => ["agent-runs", sessionId] as const,
  /** Where a version shown view only came from: its source draft and session. A version's source never changes. */
  versionSource: (key: string) => ["version-source", key] as const,
  audit: (tenantId: string | null) => ["audit", tenantId] as const,
  members: (tenantId: string | null) => ["members", tenantId] as const,
  accessSettings: (tenantId: string | null) => ["access-settings", tenantId] as const,
  modelSettings: (tenantId: string | null) => ["model-settings", tenantId] as const,
  askThreads: (tenantId: string | null, userId: string | null) => ["ask-threads", tenantId, userId] as const,
  feedback: (tenantId: string | null) => ["feedback", tenantId] as const,
  feedbackStatus: (tenantId: string | null) => ["feedback-status", tenantId] as const,
  skills: (tenantId: string | null) => ["skills", tenantId] as const,
  liveValidate: (contentJson: string) => ["live-validate", contentJson] as const,
  /** Keyed by the content it was listed from, so any change to the draft lists it again. */
  killerRecommendations: (draftId: string | null | undefined, page: number | string, contentJson: string) => ["killer-recommendations", draftId, page, contentJson] as const,
  assessment: (userId: string | null, workspaceId: string | null, productKey: string) => ["guided-assessment", userId, workspaceId, productKey] as const,
  signInConfig: () => ["sign-in-config"] as const,
};

/** What a Guided Packaging action changes on the server. */
export type PackagingAction = "stepMove" | "validate" | "edit" | "assistantMarks" | "dispose" | "agentWork" | "publish" | "competitors";
export type Action = PackagingAction | "catalogImport" | "productAdd" | "productEdit" | "productLineRename" | "accessChange" | "modelSettings" | "skills" | "feedback" | "askThreads" | "customerChange";

/**
 * The single place that says which data each action changes (read from the API handlers). After an action only
 * these roots are refetched, and only the ones a screen is showing; nothing else is touched.
 */
export const INVALIDATES: Record<Action, readonly string[]> = {
  stepMove: ["open-sessions"],
  validate: ["open-sessions"],
  edit: ["open-drafts", "catalog"],
  // Keep, Remove, Undo and Keep all: the marks live in the draft's content, and Remove and Undo change the classification history.
  assistantMarks: ["open-drafts", "catalog"],
  dispose: ["open-drafts"],
  agentWork: ["open-sessions", "open-drafts", "catalog", "agent-runs"],
  // Publishing closes the draft and its session, and writes the product's next version and the catalog.
  publish: ["open-sessions", "open-drafts", "versions", "catalog", "tenant-open-sessions", "audit"],
  competitors: ["open-sessions"],
  catalogImport: ["open-sessions", "open-drafts"],
  // A product added by hand is a new draft and its session. Nothing is published, and the Catalog reads its product line from the drafts.
  productAdd: ["open-sessions", "open-drafts"],
  // Edit product changes only the product's draft: the Catalog reads a product's name and line from its drafts.
  productEdit: ["open-drafts"],
  // A line rename changes the products and product_lines rows (the catalog) and the company's open drafts.
  productLineRename: ["catalog", "open-drafts"],
  accessChange: ["access-settings", "members"],
  modelSettings: ["model-settings"],
  skills: ["skills"],
  feedback: ["feedback"],
  askThreads: ["ask-threads"],
  customerChange: ["workspaces"],
};

/**
 * Refetches what the action changed. Resolves once every refetch is done, so callers can await it, and rejects when the refetch of a
 * query on screen fails: the caller must tell the person, because that screen is still showing the data from before the action.
 * It rejects only after every root has finished, with the first failure, so a lock a caller frees after it never frees while a refetch is still running.
 */
export async function invalidateAfter(qc: QueryClient, action: Action): Promise<void> {
  const results = await Promise.allSettled(INVALIDATES[action].map((root) => qc.invalidateQueries({ queryKey: [root] }, { throwOnError: true })));
  // A refetch cancelled by a newer one is not a failure: the newer refetch reads the same data and reports its own result.
  const failed = results.find((result): result is PromiseRejectedResult => result.status === "rejected" && !isCancelledError(result.reason));
  if (failed) throw failed.reason;
}
